<template>
    <Card>
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_RECONCILIATION">
            <div>
                <AllowPermission :permission="PERMISSION_CODES.CMS_CREATE_RECONCILIATION">
                    <div class="reconciliation-list__actions">
                        <router-link to="/reconciliations/new">
                            <Button type="button" :noMargin="true">Start inventory count</Button>
                        </router-link>
                    </div>
                </AllowPermission>
                <form class="" style="gap: 10px; max-width: 420px" @submit.prevent="fetchData">
                    <div class="d-flex align-items-end" style="gap: 10px">
                        <div class="">
                            <p class="my-2">Start Date</p>
                            <div>
                                <input
                                    class="form-control form-control-sm"
                                    type="date"
                                    id="reconciliation-start-date"
                                    :max="maxStartDate"
                                    v-model="startDate"
                                />
                            </div>
                        </div>
                        <div class="">
                            <p class="my-2">End Date</p>
                            <div>
                                <input
                                    type="date"
                                    class="form-control form-control-sm"
                                    id="reconciliation-end-date"
                                    :min="minEndDate"
                                    :max="maxEndDate"
                                    v-model="endDate"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="my-3">
                        <p class="my-2">Select Warehouse</p>
                        <SelectDropdown
                            v-if="warehouses.length"
                            id="reconciliation-warehouse"
                            :options="warehouses"
                            :value="selectedWarehouse"
                            @change="selectedWarehouse = $event"
                        />
                    </div>
                    <div class="my-3">
                        <label class="my-2 d-block" for="reconciliation-status">Review Status</label>
                        <select id="reconciliation-status" class="form-control form-control-sm" v-model="selectedStatus">
                            <option value="">All statuses</option>
                            <option value="PENDING_REVIEW">Pending review</option>
                            <option value="APPROVED">Approved</option>
                            <option value="REJECTED">Rejected</option>
                        </select>
                    </div>
                    <div class="d-flex" style="gap: 10px">
                        <Button
                            type="button"
                            variant="danger"
                            :noMargin="true"
                            :disabled="loading"
                            @click="clearFilters"
                        >{{
                            loading ? "Please wait..." : "Clear Filters"
                        }}</Button>
                        <Button type="submit" :noMargin="true" :disabled="loading">{{
                            loading ? "Please wait..." : "Load Data"
                        }}</Button>
                    </div>
                </form>
            </div>
            <UiState v-if="loading" type="loading" title="Loading reconciliations" compact />
            <UiState
                v-else-if="loadError"
                type="error"
                :message="loadError"
                actionLabel="Try again"
                compact
                @action="fetchData"
            />
            <UiState
                v-else-if="!reconciliations.length"
                type="empty"
                title="No reconciliations found"
                compact
            />

            <CustomTable v-else>
                <template #head>
                    <tr>
                        <th>Name</th>
                        <th>Created By</th>
                        <th>Warehouse</th>
                        <th>Room Type</th>
                        <th>Count Setup</th>
                        <th>Assigned Users</th>
                        <th>Created On</th>
                        <th>Status</th>
                        <th>Products</th>
                        <th>Actions</th>
                    </tr>
                </template>
                <template #body>
                    <tr v-for="reconciliation of reconciliations" :key="reconciliation.id">
                        <td>{{ reconciliation.name }}</td>
                        <td>{{ reconciliation.modified_by }}</td>
                        <td>{{ reconciliation.warehouse_name }}</td>
                        <td>{{ reconciliation.is_back_room ? "Back Room" : "Front Room" }}</td>
                        <td>
                            <span v-if="reconciliation.blind_count">Blind · </span>{{ countTypeLabel(reconciliation.count_type) }}
                            <span v-if="reconciliation.exclude_zero_stock"> · non-zero stock</span>
                        </td>
                        <td>{{ assignedUsersLabel(reconciliation) }}</td>
                        <td>{{ usDateFormat(reconciliation.createdAt) }}</td>
                        <td>
                            <span :class="['review-status', `review-status--${statusKey(reconciliation.status)}`]">
                                {{ statusLabel(reconciliation.status) }}
                            </span>
                        </td>
                        <td>{{ reconciliation.total_products }}</td>
                        <td>
                            <router-link :to="`/reconciliations/${reconciliation.id}`">
                                <Button style="min-width: 70px; padding: 5px" :noMargin="true"> View </Button>
                            </router-link>
                            <!-- <Button
                                @click="deleteReconciliation(reconciliation.id)"
                                style="min-width: 70px; padding: 5px; background: red"
                                class="mx-2"
                                :noMargin="true"
                                >Delete</Button
                            > -->
                        </td>
                    </tr>
                </template>
            </CustomTable>
            <Paginator
                v-if="!loading && !loadError && reconciliations.length"
                :totalPages="totalPages"
                :page="page"
                @change="page = $event"
            />
        </AllowPermission>
    </Card>
</template>

<script>
import { mapActions } from "vuex"
import CustomTable from "../../components/layout/CustomTable.vue"
import { http } from "../../utils/http"
import Card from "../../components/UI/Card.vue"
import Paginator from "../../components/other/Paginator.vue"
import Button from "../../components/UI/Button.vue"
import UiState from "../../components/UI/UiState.vue"
import { confirmDialog, usDateFormat } from "../../utils/common"
import { mapState } from "vuex"
import SelectDropdown from "../../components/crud/SelectDropdown.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import { PERMISSION_CODES } from "../../store/permission"

export default {
    data() {
        return {
            PERMISSION_CODES,
            reconciliations: [],
            totalPages: 0,
            page: 1,
            loading: true,
            loadError: "",
            limit: 10,
            startDate: "",
            endDate: "",
            selectedWarehouse: "",
            selectedStatus: "PENDING_REVIEW",
        }
    },
    components: {
        SelectDropdown,
        Button,
        UiState,
        Paginator,
        Card,
        CustomTable,
        AllowPermission,
    },
    mounted() {
        this.setData({
            pageTitle: "Reconciliations",
        })
        this.initDates()
        this.fetchData()
    },
    computed: {
        ...mapState("misc", ["itemGroups", "warehouses"]),
        maxStartDate() {
            const today = this.localDateString(new Date())
            return this.endDate && this.endDate < today ? this.endDate : today
        },
        minEndDate() {
            return this.startDate || null
        },
        maxEndDate() {
            return this.localDateString(new Date())
        },
    },
    methods: {
        usDateFormat,
        ...mapActions("misc", ["setData"]),
        localDateString(date) {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, "0")
            const day = String(date.getDate()).padStart(2, "0")
            return `${year}-${month}-${day}`
        },
        initDates() {
            const today = new Date()
            this.startDate = this.localDateString(new Date(today.getFullYear(), today.getMonth(), 1))
            this.endDate = this.localDateString(today)
        },
        async clearFilters() {
            this.startDate = ""
            this.endDate = ""
            this.selectedWarehouse = ""
            this.selectedStatus = ""

            if (this.page === 1) {
                await this.fetchData()
            } else {
                this.page = 1
            }
        },
        async deleteReconciliation(id) {
            try {
                const confirmed = await confirmDialog({
                    title: "Delete reconciliation?",
                    text: "This reconciliation record will be permanently removed.",
                    confirmButtonText: "Delete",
                })
                if (!confirmed) return

                this.loading = true
                this.reconciliations = []
                await http.delete(`/reconciliations/${id}`)
                this.reconciliations = this.reconciliations.filter((item) => item.id != id)
                this.$toast.success("Reconciliation deleted successfully")
            } catch (err) {
                console.log("error in fetching reconciliations", err)
            }
        },
        async fetchData() {
            try {
                this.loadError = ""
                this.loading = true
                this.reconciliations = []
                const params = new URLSearchParams({
                    page: String(this.page),
                    limit: String(this.limit),
                    warehouse_id: String(this.selectedWarehouse || ""),
                    status: this.selectedStatus || "",
                    start_date: this.startDate || "",
                    end_date: this.endDate || "",
                })
                const res = await http.get(`/reconciliations?${params.toString()}`)
                this.reconciliations = res.data.result
                this.totalPages = res.data.totalPages
            } catch (err) {
                console.log("error in fetching reconciliations", err)
                this.loadError = "Could not load reconciliations."
            }
            this.loading = false
        },
        statusKey(status) {
            return String(status || "PENDING_REVIEW").toLowerCase().replace("_", "-")
        },
        statusLabel(status) {
            const labels = {
                PENDING_REVIEW: "Pending review",
                APPROVED: "Approved",
                REJECTED: "Rejected",
            }
            return labels[status] || labels.PENDING_REVIEW
        },
        countTypeLabel(value) {
            return String(value || "LOT_PACKAGE").toUpperCase() === "PRODUCT" ? "By product" : "By lot / package"
        },
        assignedUsersLabel(reconciliation) {
            const value = reconciliation && reconciliation.assigned_user_ids
            if (Array.isArray(value)) return value.length ? value.join(", ") : "All authorized users"
            if (typeof value === "string" && value.trim()) {
                try {
                    const parsed = JSON.parse(value)
                    if (Array.isArray(parsed)) return parsed.join(", ") || "All authorized users"
                } catch (err) {
                    return value
                }
                return value
            }
            return "All authorized users"
        },
    },
    watch: {
        page() {
            this.fetchData()
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

form {
  gap: $sectionGap !important;
}

.d-flex {
  gap: $sectionGap !important;
}

.reconciliation-list__actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: $sectionGap;
}

.review-status {
  display: inline-flex;
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba($warningColor, 0.14);
  color: #7a4a00;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.review-status--approved {
  background: rgba($successColor, 0.14);
  color: #287a4a;
}

.review-status--rejected {
  background: rgba($dangerColor, 0.12);
  color: #b42318;
}

:deep(.dark) .review-status {
  color: $warningColorDark;
}

:deep(.dark) .review-status--approved {
  color: $successColorDark;
}

:deep(.dark) .review-status--rejected {
  color: #ff8a80;
}

@media (max-width: 640px) {
  form,
  form > .d-flex {
    width: 100%;
  }

  form > .d-flex {
    align-items: stretch !important;
    flex-direction: column;
  }
}

/* Right-align numeric columns where applicable */
::deep(table) {
  thead th:nth-child(7) {
    text-align: right;
  }
  tbody td:nth-child(7) {
    text-align: right;
  }
}
</style>
